import {ok, Result} from "neverthrow"
import {createResource, ErrorDocument, getFile, getResource, OkDocument, Permissible, updateResource} from "./common"
import {BusinessFBOCustomer, IndividualCustomer} from "./customer"
import {Cents} from "./transaction"
import {AccountsTotalCreditLimit} from "./accountsTotalCreditLimit"
import {AccountCustomerType} from "../components/Accounts/Accounts.types"
import type {CustomerInterestTier, InterestTieringMode} from "./depositProduct"

// DACA (Deposit Account Control Agreement) types
export type DacaPolicyPurpose = "scheduledAutoClear" | "manualTransfersByOperator"

export type DacaCounterpartyAddress = {
	street: string
	city: string
	state: string
	postalCode: string
	country: string
}

export type AchCounterparty = {
	routingNumber: string
	accountNumber: string
	accountType: string
	name: string
}

export type WireCounterparty = {
	routingNumber: string
	accountNumber: string
	name: string
	address?: DacaCounterpartyAddress
}

// DacaPolicies matches the Scala case class serialization:
// purpose is directly on the object, not nested under dacPolicies
export type DacaPolicies = {
	purpose: DacaPolicyPurpose[]
	achCounterparty?: AchCounterparty
	wireCounterparty?: WireCounterparty
	paymentMethod?: "ach" | "wire"
}

export type FinancialBusinessFBOAccount = {
	type: "financialBusinessFBOAccount"
	id: string
	attributes: {
		createdAt: Date
		updatedAt?: Date
		name: string
		depositProduct: string
		routingNumber: string
		accountNumber: Permissible<string>
		maskedAccountNumber?: string
		secondaryAccountNumber?: {
			routingNumber: string
			accountNumber: string
		}
		balance: number
		hold: number
		reserve: number
		available: number
		overdraftLimit?: number
		currency: string
		tags: {[key: string]: string}
		status: AccountStatus
		dacaStatus?: DacaStatusType
		dacaPolicies?: DacaPolicies
		closeReason?: string
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
		isOverdrawnWithinLimit?: boolean
		interestTerms?: InterestTerms
	}
	relationships: {
		customer: {
			data: {
				type: "customer"
				id: string
			}
		}
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
	}
	included?: BusinessFBOCustomer[]
}

export type DepositAccount = {
	type: "depositAccount"
	id: string
	attributes: {
		createdAt: Date
		updatedAt?: Date
		name: string
		depositProduct: string
		routingNumber: string
		accountNumber: Permissible<string>
		maskedAccountNumber?: string
		secondaryAccountNumber?: {
			routingNumber: string
			accountNumber: string
		}
		balance: number
		hold: number
		reserve: number
		available: number
		overdraftLimit?: number
		currency: string
		tags: {[key: string]: string}
		status: AccountStatus
		dacaStatus?: DacaStatusType
		dacaPolicies?: DacaPolicies
		closeReason?: string
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
		isOverdrawnWithinLimit?: boolean
		interestTerms?: InterestTerms
	}
	relationships: {
		customer?: {
			data: {
				type: "customer"
				id: string
			}
		}
		customers?: {
			data: [
				{
					type: "customer"
					id: string
				}
			]
		}
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
	}
	included?: IndividualCustomer[]
}

export type AccountsMetrics = {
	type: "accountsMetrics"
	attributes: {
		numberOfBatchAccounts: number
		numberOfDepositAccounts: number
		numberOfOrgFBOAccounts: number
		numberOfFinancialBusinessFBOAccounts: number
		totalBatchBalance: number
		totalDepositBalance: number
		totalOrgFBOBalance: number
		totalFinancialBusinessFBOBalance: number
	}
}

export type AccountDepositProduct = {
	type: "accountDepositProduct"
	id: string
	attributes: {
		name: string
	}
}

export type AccountWalletTerms = {
	type: "accountWalletTerms"
	attributes: {
		name: string
	}
}

export type ChartData = {
	date: Date
	balance: Cents
}

export type TotalDepositChartData = {
	type: "chartData"
	attributes: {
		data: [ChartData]
	}
}

export type BatchAccount = {
	type: "batchAccount"
	id: string
	attributes: {
		createdAt: Date
		updatedAt?: Date
		name: string
		depositProduct: string
		routingNumber: string
		accountNumber: Permissible<string>
		maskedAccountNumber?: string
		balance: number
		hold: number
		reserve: number
		currency: string
		status: AccountStatus.Open | AccountStatus.Closed
		closeReason?: string
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
	}
}

export type OrgGeneralLedgerAccount = {
	type: "orgGeneralLedgerAccount"
	id: string
	attributes: {
		createdAt: Date
		updatedAt?: Date
		name: string
		balance: number
		hold: number
		reserve: number
		currency: string
		status: AccountStatus.Open | AccountStatus.Closed
		tags: {[key: string]: string}
		closeReason?: string
		closeReasonText?: string
		fraudReason?: FraudReason
		bankReason?: BankReason
		freezeReason?: string
		category: Category
		available: Cents
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
	}
}

export type GLAccount = {
	type: "glAccount"
	id: string
	attributes: {
		createdAt: Date
		updatedAt?: Date
		name: string
		balance: number
		hold: number
		reserve: number
		category: Category
		status: AccountStatus.Open | AccountStatus.Closed
		closeReason?: string
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
	}
	relationships: {
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
	}
}

export type CreditGLAccount = {
	type: "creditGLAccount"
	id: string
	attributes: {
		createdAt: Date
		updatedAt?: Date
		name: string
		balance: number
		hold: number
		reserve: number
		category: Category
		status: AccountStatus.Open | AccountStatus.Closed
		closeReason?: string
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
	}
	relationships: {
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
	}
}

export type CreditAccount = {
	type: "creditAccount"
	id: string
	attributes: {
		createdAt: Date
		updatedAt?: Date
		name: string
		creditTerms: string
		balance: number
		hold: number
		reserve: number
		available: number
		creditLimit: number
		currency: string
		tags: {[key: string]: string}
		status: AccountStatus
		closeReason?: CloseReason
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
	}
	relationships: {
		customer: {
			data: {
				type: "customer"
				id: string
			}
		}
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
	}
}

export type OrgLoanAccount = {
	type: "orgLoanAccount"
	id: string
	attributes: {
		createdAt: Date
		updatedAt?: Date
		name: string
		balance: number
		hold: number
		reserve: number
		available: number
		creditLimit: number
		currency: string
		tags: {[key: string]: string}
		status: AccountStatus
		closeReason?: CloseReason
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
	}
	relationships: {
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
		borrowingOrg: {
			data: {
				type: "org"
				id: string
			}
		}
	}
}

export type MerchantCashAdvanceAccount = {
	type: "merchantCashAdvanceAccount"
	id: string
	attributes: {
		createdAt: Date
		updatedAt?: Date
		name: string
		balance: number
		hold: number
		reserve: number
		available: number
		currency: string
		tags: {[key: string]: string}
		status: AccountStatus
		closeReason?: CloseReason
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
	}
	relationships: {
		customer: {
			data: {
				type: "customer"
				id: string
			}
		}
		org: {
			data: {
				type: "org"
				id: string
			}
		}
	}
}

export type Wallet = {
	type: "walletAccount"
	id: string
	attributes: {
		name: string
		createdAt: Date
		walletTerms: string
		balance: number
		hold: number
		available: number
		reserve: number
		routingNumber: string
		accountNumber: Permissible<string>
		tags: {[key: string]: string}
		currency: string
		status: AccountStatus
		closeReason?: string
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
		updatedAt?: Date
		bankDetails?: {name: string; address: string}
	}
	relationships: {
		customer?: {
			data: {
				type: "customer"
				id: string
			}
		}
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
		orgGLAccount?: {
			data: {
				type: "orgGLAccount"
				id: string
			}
		}
		orgFBOAccount?: {
			data: {
				type: "orgFBOAccount"
				id: string
			}
		}
	}
	included?: IndividualCustomer[]
}

export type OrgFBO = {
	type: "fboAccount"
	id: string
	attributes: {
		name: string
		createdAt: Date
		balance: number
		hold: number
		available: number
		reserve: number
		routingNumber: string
		accountNumber: Permissible<string>
		tags: {[key: string]: string}
		currency: string
		status: AccountStatus
		closeReason?: string
		fraudReason?: FraudReason
		bankReason?: BankReason
		closeReasonText?: string
		freezeReason?: string
		updatedAt?: Date
		bankDetails?: {name: string; address: string}
	}
	relationships: {
		customer?: {
			data: {
				type: "customer"
				id: string
			}
		}
		org: {
			data: {
				type: "org"
				id: string
			}
		}
		bank: {
			data: {
				type: "bank"
				id: string
			}
		}
	}
	included?: IndividualCustomer[]
}

export type Category = "Asset" | "Liability"

export enum CloseReason {
	Fraud = "Fraud",
	ByCustomer = "ByCustomer",
	NegativeBalance = "NegativeBalance",
	ProgramChange = "ProgramChange",
	BreachOfTermsAndConditions = "BreachOfTermsAndConditions",
	ByBank = "ByBank",
	NoAccountActivity = "NoAccountActivity",
	CustomerDeceased = "CustomerDeceased",
}

export enum OverdraftStatus {
	Positive = "NoOverdraft",
	OverdrawnWithinLimit = "OverdrawnWithinLimit",
	Negative = "Overdrawn",
}

export enum AccountStatus {
	Open = "Open",
	Closed = "Closed",
	Frozen = "Frozen",
}

export enum FraudReason {
	ACHActivity = "ACHActivity",
	CardActivity = "CardActivity",
	CheckActivity = "CheckActivity",
	ApplicationHistory = "ApplicationHistory",
	AccountActivity = "AccountActivity",
	ClientIdentified = "ClientIdentified",
	IdentityTheft = "IdentityTheft",
	LinkedToFraudulentCustomer = "LinkedToFraudulentCustomer",
}

export type InterestTerms = {
	interestFormula?: string | undefined
	customerInterestShare: number | undefined
	enableCustomerInterestFormula: boolean | undefined
	customerInterestFormula: string | undefined
	interestRate: number | undefined
	customerInterestRate: number | undefined
	enableTieredCustomerInterestFormula?: boolean
	customerInterestTieringMode?: InterestTieringMode
	customerInterestTiers?: CustomerInterestTier[]
}

export enum DacaStatusType {
	Entered = "Entered",
	Activated = "Activated",
}

export enum BankReason {
	ProhibitedBusiness = "ProhibitedBusiness",
	MissingCddEdd = "MissingCddEdd",
	NonUsOperations = "NonUsOperations",
	SuspectedFraud = "SuspectedFraud",
	Unfunded = "Unfunded",
	Inactive = "Inactive",
}

export type Account =
	| DepositAccount
	| GLAccount
	| CreditGLAccount
	| BatchAccount
	| OrgGeneralLedgerAccount
	| CreditAccount
	| OrgLoanAccount
	| MerchantCashAdvanceAccount
	| FinancialBusinessFBOAccount
	| Wallet
	| OrgFBO

export type AccountStatusAudit = {
	type: "accountStatusAudit"
	attributes: {
		setBy?: string
		setAt?: Date
	}
	relationships: {
		account: {
			data: {
				type: "account"
				id: string
			}
		}
	}
}

export type AccountWithOwners = DepositAccount & IndividualCustomer[]

export type AchLimits = {
	dailyDebit: number
	dailyCredit: number
	monthlyDebit: number
	monthlyDebitSoft: number
	dailyDebitSoft: number
	monthlyCredit: number
}

export type CardLevelLimits = {
	dailyWithdrawal: number
	dailyDeposit: number
	dailyPurchase: number
	dailyCardTransaction: number
}

export type CheckLimits = {
	daily: number
	monthly: number
	dailySoft: number
	monthlySoft: number
}

export type AchTotalsDaily = {
	debits: number
	credits: number
}
export type AchTotalsMonthly = {
	debits: number
	credits: number
}

export type CardTotals = {
	withdrawals: number
	deposits: number
	purchases: number
	cardTransactions: number
}

export type CheckTotalsDaily = number
export type CheckTotalsMonthly = number

export type WireLimits = {
	dailyTransfer: number
	monthlyTransfer: number
	dailyTransferSoft: number
	monthlyTransferSoft: number
}

export type WireTotalsDaily = {
	transfers: number
}
export type WireTotalsMonthly = {
	transfers: number
}

export type CheckPaymentLimits = {
	dailySent?: number
	monthlySent?: number
	dailySentSoft?: number
	monthlySentSoft?: number
}

export type CheckPaymentTotalsDaily = {
	sent: number
}
export type CheckPaymentTotalsMonthly = {
	sent: number
}

export interface OverrideInfoBase {
	status: "Active" | "Canceled" | "Expired"
	expiredAt?: string
}
export interface AchLimitOverrideInfo extends OverrideInfoBase {
	values: {
		dailyDebit?: number
		dailyCredit?: number
		monthlyDebit?: number
		monthlyCredit?: number
	}
}

export interface CardLimitOverrideInfo extends OverrideInfoBase {
	values: {
		dailyWithdrawal?: number
		dailyDeposit?: number
		dailyPurchase?: number
		dailyCardTransaction?: number
	}
}

export interface CheckLimitOverrideInfo extends OverrideInfoBase {
	values: {
		daily?: number
		monthly?: number
	}
}

export interface WireLimitOverrideInfo extends OverrideInfoBase {
	values: {
		daily?: number
		monthly?: number
	}
}

export interface CheckPaymentLimitOverrideInfo extends OverrideInfoBase {
	values: {
		daily?: number
		monthly?: number
	}
}

export type Limits = {
	type: "limits"
	attributes: {
		ach: {
			limits: AchLimits
			totalsDaily: AchTotalsDaily
			totalsMonthly: AchTotalsMonthly
			override?: AchLimitOverrideInfo
		}
		card: {
			limits: CardLevelLimits
			totalsDaily: CardTotals
			override?: CardLimitOverrideInfo
		}
		checkDeposit: {
			limits: CheckLimits
			totalsDaily: CheckTotalsDaily
			totalsMonthly: CheckTotalsMonthly
			override?: CheckLimitOverrideInfo
		}
		wire: {
			limits: WireLimits
			totalsDaily: WireTotalsDaily
			totalsMonthly: WireTotalsMonthly
			override?: WireLimitOverrideInfo
		}
		checkPayment: {
			limits: CheckPaymentLimits
			totalsDaily: CheckPaymentTotalsDaily
			totalsMonthly: CheckPaymentTotalsMonthly
			override?: CheckPaymentLimitOverrideInfo
		}
	}
}

export enum AccountType {
	deposit = "deposit",
	batch = "batch",
	gl = "gl",
	creditGL = "creditGL",
	credit = "credit",
	orgGeneralLedger = "orgGeneralLedger",
	orgLoan = "orgLoan",
	financialBusinessFBO = "financialBusinessFBO",
	wallet = "wallet",
	fbo = "fbo",
	merchantCashAdvance = "merchantCashAdvance",
}

export interface FindAccountsRequestParam {
	accessToken: string
	offset: number
	limit?: number
	sort?: string
	searchQuery?: string
	overdraftStatus?: string[]
	status?: AccountStatus[]
	dacaStatus?: DacaStatusType[]
	orgs?: string[]
	banks?: string[]
	types: (keyof typeof AccountType)[]
	customerId?: string
	fromBalance?: Cents | ""
	toBalance?: Cents | ""
	depositProductId?: number
	depositProducts?: string[]
	walletTermsId?: number
	walletTermsIds?: string[]
	customerTypes?: (keyof typeof AccountCustomerType)[]
	requestType: "json" | "csv"
	orgFBOAccount?: string
	extraFields?: {
		account?: "maskedAccountNumber"
	}
}

export async function findAccounts(requestParam: FindAccountsRequestParam) {
	const query = {
		page: {
			limit: requestParam.limit,
			offset: requestParam.offset,
		},
		filter: {
			query: requestParam.searchQuery != "" ? requestParam.searchQuery : null,
			overdraftStatus: requestParam.overdraftStatus,
			status: requestParam.status,
			dacaStatus: requestParam.dacaStatus,
			orgs: requestParam.orgs && requestParam.orgs.length > 0 ? requestParam.orgs : null,
			banks: requestParam.banks && requestParam.banks.length > 0 ? requestParam.banks : null,
			types: requestParam.types,
			customerTypes: requestParam.customerTypes,
			customerId: requestParam.customerId,
			fromBalance: requestParam.fromBalance != "" ? requestParam.fromBalance : null,
			toBalance: requestParam.toBalance != "" ? requestParam.toBalance : null,
			depositProductId: requestParam.depositProductId,
			depositProducts:
				requestParam.depositProducts && requestParam.depositProducts.length > 0 ? requestParam.depositProducts : null,
			walletTermsId: requestParam.walletTermsId,
			walletTermsIds:
				requestParam.walletTermsIds && requestParam.walletTermsIds.length > 0 ? requestParam.walletTermsIds : null,
		},
		sort: requestParam.sort,
		include: "org,bank",
		extraFields: requestParam.extraFields,
	}

	switch (requestParam.requestType) {
		case "csv":
			return await getFile("accounts", requestParam.accessToken, query)
		default:
			return await getResource<Array<Account>>("accounts", requestParam.accessToken, query)
	}
}

export async function findAccountWallets(requestParam: FindAccountsRequestParam) {
	const query = {
		page: {
			limit: requestParam.limit,
			offset: requestParam.offset,
		},
		filter: {
			query: requestParam.searchQuery != "" ? requestParam.searchQuery : null,
			overdraftStatus: requestParam.overdraftStatus,
			status: requestParam.status,
			dacaStatus: requestParam.dacaStatus,
			orgs: requestParam.orgs && requestParam.orgs.length > 0 ? requestParam.orgs : null,
			banks: requestParam.banks && requestParam.banks.length > 0 ? requestParam.banks : null,
			customerId: requestParam.customerId,
			fromBalance: requestParam.fromBalance != "" ? requestParam.fromBalance : null,
			toBalance: requestParam.toBalance != "" ? requestParam.toBalance : null,
		},
		sort: requestParam.sort,
		include: "org,bank",
		extraFields: requestParam.extraFields,
	}

	switch (requestParam.requestType) {
		case "csv":
			return await getFile(`accounts/${requestParam.orgFBOAccount}/wallets`, requestParam.accessToken, query)
		default:
			return await getResource<Array<Account>>(
				`accounts/${requestParam.orgFBOAccount}/wallets`,
				requestParam.accessToken,
				query
			)
	}
}

export async function findAccountsHandler(requestParam: FindAccountsRequestParam) {
	return requestParam.orgFBOAccount ? findAccountWallets(requestParam) : findAccounts(requestParam)
}

export async function getRevenueAccounts(accessToken: string, bankId: string) {
	if (bankId === "0") return ok([])

	const query = {
		page: {
			limit: 1000,
			offset: 0,
		},
		filter: {
			orgId: "1",
			bankId,
		},
	}

	const result = await getResource<Array<Account>>("accounts", accessToken, query)

	return result.map((v) =>
		v.data.filter(isDepositAccount).filter((a: DepositAccount) => a.attributes.tags["purpose"] === "revenue")
	)
}

export async function getReserveAccounts(accessToken: string, bankId: string) {
	if (bankId === "0") return ok([])

	const query = {
		page: {
			limit: 1000,
			offset: 0,
		},
		filter: {
			orgId: "1",
			bankId,
		},
	}

	const result = await getResource<Array<Account>>("accounts", accessToken, query)

	return result.map((v) =>
		v.data.filter(isDepositAccount).filter((a: DepositAccount) => a.attributes.tags["purpose"] === "reserve")
	)
}

export async function getSponsoredAccounts(accessToken: string, bankId: string) {
	if (bankId === "0") return ok([])

	const query = {
		page: {
			limit: 1000,
			offset: 0,
		},
		filter: {
			orgId: "1",
			bankId,
		},
	}

	const result = await getResource<Array<Account>>("accounts", accessToken, query)

	return result.map((v) =>
		v.data.filter(isDepositAccount).filter((a: DepositAccount) => a.attributes.tags["purpose"] === "sponsoredInterest")
	)
}

export async function getOverdraftReserveAccounts(accessToken: string, bankId: string) {
	if (bankId === "0") return ok([])

	const query = {
		page: {
			limit: 1000,
			offset: 0,
		},
		filter: {
			orgId: "1",
			bankId,
			tags: JSON.stringify({purpose: "overdraft_reserve"}),
		},
	}

	const result = await getResource<Array<Account>>("accounts", accessToken, query)
	return result.map((v) =>
		v.data.filter(isDepositAccount).filter((a: DepositAccount) => a.attributes.tags["purpose"] === "overdraft_reserve")
	)
}

export async function getOrgGeneralLedgerAccounts(accessToken: string, bankId: string, orgId: string) {
	if (bankId === "0") return ok([])

	const query = {
		page: {
			limit: 1000,
			offset: 0,
		},
		filter: {
			orgId: orgId,
			type: AccountType.orgGeneralLedger,
			...(bankId ? {bankId} : {}),
		},
	}

	const result = await getResource<Array<Account>>("accounts", accessToken, query)
	return result.map((v) => v.data.filter(isOrgGeneralLedgerAccount))
}

export async function getOrgLoanAccounts(
	accessToken: string,
	bankId: string,
	orgId: string
): Promise<Result<OrgLoanAccount[], ErrorDocument>> {
	if (bankId === "0") return ok([])

	const query = {
		page: {
			limit: 1000,
			offset: 0,
		},
		filter: {
			type: AccountType.orgLoan,
			...(bankId ? {bankId} : {}),
		},
	}

	const result = await getResource<Array<Account>>("accounts", accessToken, query)
	return result.map((v) => v.data.filter(isOrgLoanAccount).filter((x) => x.relationships.borrowingOrg.data.id == orgId))
}

export async function getBankGLAccounts(accessToken: string, bankId: string) {
	const query = {
		page: {
			limit: 1000,
			offset: 0,
		},
		filter: {
			bankId: bankId,
			type: AccountType.gl,
		},
	}

	const result = await getResource<Array<Account>>("accounts", accessToken, query)
	return result
}

export async function getAccountsWithPurpose(accessToken: string, bankId: string, orgId: string, purpose: string) {
	if (bankId === "0") return ok([])

	const query = {
		page: {
			limit: 1000,
			offset: 0,
		},
		filter: {
			orgId: orgId,
			bankId,
			tags: JSON.stringify({purpose: purpose}),
		},
	}
	const result = await getResource<Array<Account>>("accounts", accessToken, query)

	return result.map((v) =>
		v.data.filter(isDepositAccount).filter((a: DepositAccount) => a.attributes.tags["purpose"] === purpose)
	)
}

export async function getCustomerAccounts(accessToken: string, offset: number, limit: number, customerId: string) {
	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			customerId,
		},
		extraFields: {
			account: "maskedAccountNumber",
		},
	}

	const result = await getResource<Array<DepositAccount>>("accounts", accessToken, query)

	return result.map((v) => v.data)
}

export async function getAccount<Account>(accessToken: string, id: string): Promise<Result<Account, ErrorDocument>> {
	const result = await getResource<Account>(`accounts/${id}`, accessToken, {
		extraFields: {
			account: "maskedAccountNumber,interestTerms",
		},
	})

	return result.map((v) => v.data)
}

export async function getAccountWithOwners<AccountWithOwners>(accessToken: string, id: string) {
	const result = await getResource<AccountWithOwners>(`accounts/${id}`, accessToken, {
		include: "customer,org",
		extraFields: {
			account: "maskedAccountNumber,interestTerms",
		},
	})
	return result.map((v) => {
		return {account: v.data, included: v.included}
	})
}

export async function getAccountStatusAudit(accessToken: string, id: string) {
	const result = await getResource<AccountStatusAudit>(`accounts/${id}/status-audit`, accessToken)
	return result.map((v) => v.data)
}

export function isDepositAccount(account: any): account is DepositAccount {
	return account.type === "depositAccount"
}

export function isDepositOrFBOAccount(account: any): account is DepositAccount | FinancialBusinessFBOAccount {
	return isDepositAccount(account) || isFinancialBusinessFBOAccount(account)
}

export function isDepositOrFBOAccountOrWallet(
	account: any
): account is DepositAccount | FinancialBusinessFBOAccount | Wallet {
	return isDepositAccount(account) || isFinancialBusinessFBOAccount(account) || isWalletAccount(account)
}

export function isFinancialBusinessFBOAccount(account: any): account is FinancialBusinessFBOAccount {
	return account.type === "financialBusinessFBOAccount"
}

export function isOrgGeneralLedgerAccount(account: Account): account is OrgGeneralLedgerAccount {
	return account.type === "orgGeneralLedgerAccount"
}

export function isBatchAccount(account: Account): account is DepositAccount {
	return account.type === "batchAccount"
}

export function isCreditAccount(account: Account): account is CreditAccount {
	return account.type === "creditAccount"
}

export function isCreditOperationalAccount(account: Account): account is DepositAccount {
	return isDepositAccount(account) && account.attributes?.tags?.purpose === "credit_operational_account"
}

export function isWalletAccount(account: any): account is Wallet {
	return account.type === "walletAccount"
}

export function isOrgFBOAccount(account: any): account is OrgFBO {
	return account.type === "fboAccount"
}

export function isDepositOrBatchOrFBOAccount(
	account: Account
): account is DepositAccount | BatchAccount | FinancialBusinessFBOAccount {
	return isDepositOrFBOAccount(account) || isBatchAccount(account)
}

export function isDepositOrBatchOrFBOAccountOrWallet(
	account: Account
): account is DepositAccount | BatchAccount | FinancialBusinessFBOAccount | Wallet {
	return isDepositOrFBOAccount(account) || isBatchAccount(account) || isWalletAccount(account)
}

export function hasAccountNumber(
	account: Account
): account is DepositAccount | BatchAccount | FinancialBusinessFBOAccount | Wallet {
	return isDepositOrFBOAccount(account) || isBatchAccount(account) || isWalletAccount(account)
}

export function isDepositOrCreditOrFBOAccountOrWallet(
	account: Account
): account is DepositAccount | CreditAccount | FinancialBusinessFBOAccount | Wallet {
	return isDepositOrFBOAccount(account) || isCreditAccount(account) || isWalletAccount(account)
}

export function isAccountResource(resource: {type?: string; id?: string}): resource is Account {
	const r = resource as {type?: string}
	return (
		r.type === "depositAccount" ||
		r.type === "creditAccount" ||
		r.type === "walletAccount" ||
		r.type === "fboAccount" ||
		r.type === "financialBusinessFBOAccount" ||
		r.type === "glAccount" ||
		r.type === "creditGLAccount" ||
		r.type === "batchAccount" ||
		r.type === "orgGeneralLedgerAccount" ||
		r.type === "orgLoanAccount" ||
		r.type === "merchantCashAdvanceAccount"
	)
}

export function getProduct(account: Account) {
	if (isDepositOrBatchOrFBOAccount(account)) {
		return account.attributes.depositProduct
	}
	if (isWalletAccount(account)) {
		return account.attributes.walletTerms
	}
	if (isCreditAccount(account)) {
		return account.attributes.creditTerms
	}
	return null
}

export function isBankInAccount(
	account: Account
): account is
	| DepositAccount
	| CreditAccount
	| FinancialBusinessFBOAccount
	| OrgGeneralLedgerAccount
	| GLAccount
	| Wallet
	| CreditGLAccount
	| OrgLoanAccount {
	return (
		isDepositOrCreditOrFBOAccountOrWallet(account) ||
		isOrgGeneralLedgerAccount(account) ||
		isGlAccount(account) ||
		isCreditGLAccount(account) ||
		isOrgLoanAccount(account)
	)
}

export function isGlAccount(account: Account): account is GLAccount {
	return account.type == "glAccount"
}

export function isCreditGLAccount(account: Account): account is CreditGLAccount {
	return account.type == "creditGLAccount"
}

export function isOrgLoanAccount(account: Account): account is OrgLoanAccount {
	return account.type == "orgLoanAccount"
}

export function isMerchantCashAdvanceAccount(account: Account): account is MerchantCashAdvanceAccount {
	return account.type === "merchantCashAdvanceAccount"
}

export function isMcaGlAccount(account: Account): boolean {
	return isGlAccount(account) && account.attributes.name === "MCA GL" && !account.relationships?.bank?.data?.id
}

export function isMcaRelatedAccount(account: Account): boolean {
	return isMerchantCashAdvanceAccount(account) || isMcaGlAccount(account)
}

export function isFrozen(account: Account): account is DepositAccount {
	return account.attributes.status === AccountStatus.Frozen
}

export function isClosed(account: Account) {
	return account.attributes.status === AccountStatus.Closed
}

export async function getBatchAccounts(accessToken: string, offset: number, limit: number, orgId?: string) {
	const query = {
		page: {
			limit,
			offset,
		},
		include: "org",
		filter: {
			type: AccountType.batch,
			...(orgId && {orgs: [orgId]}),
		},
	}

	return await getResource<Array<BatchAccount>>("accounts", accessToken, query)
}

export async function getAccountsMetrics(accessToken: string) {
	return await getResource<AccountsMetrics>("accounts/org/metrics", accessToken)
}

export async function getAccountDepositProducts(accessToken: string, id: string) {
	return await getResource<Array<AccountDepositProduct>>(`accounts/${id}/deposit-products`, accessToken)
}

export async function getAccountWalletTerms(accessToken: string, id: string) {
	return await getResource<Array<AccountWalletTerms>>(`accounts/${id}/wallet-terms`, accessToken)
}

function getCloseAccountType(account: Account) {
	if (isDepositOrFBOAccount(account)) {
		return "depositAccountClose"
	} else if (isWalletAccount(account)) {
		return "walletAccountClose"
	} else if (isCreditAccount(account)) {
		return "creditAccountClose"
	} else if (isBatchAccount(account)) {
		return "batchAccountClose"
	}
	return "accountClose"
}
export async function closeAccount(
	accessToken: string,
	account: Account,
	reason: CloseReason,
	reasonText?: string,
	fraudReason?: string,
	bankReason?: string
) {
	const data = {
		type: getCloseAccountType(account),
		attributes: {
			reason,
			reasonText,
			fraudReason,
			bankReason,
		},
	}

	return await createResource<Account>(`accounts/${account.id}/close`, accessToken, data)
}

export async function enterDaca(accessToken: string, account: Account, dacaPolicies?: DacaPolicies) {
	const data = {
		type: "updateDacaPoliciesRequest",
		...(dacaPolicies
			? {
					attributes: {
						dacaPolicies,
					},
			  }
			: {}),
		relationships: {
			account: {
				data: {
					type: "depositAccount",
					id: account.id,
				},
			},
		},
	}
	return await createResource<Account>(`accounts/${account.id}/enter-daca`, accessToken, data)
}

export async function terminateDaca(accessToken: string, account: Account) {
	return await createResource<Account>(`accounts/${account.id}/terminate-daca`, accessToken, {})
}

export async function activateDaca(accessToken: string, account: Account) {
	return await createResource<Account>(`accounts/${account.id}/activate-daca`, accessToken, {})
}

export async function deactivateDaca(accessToken: string, account: Account) {
	return await createResource<Account>(`accounts/${account.id}/deactivate-daca`, accessToken, {})
}

export async function reopenAccount(accessToken: string, account: Account) {
	return await createResource<Account>(`accounts/${account.id}/reopen`, accessToken, {})
}

export interface AccountFreezeDetails {
	reason: string
	subReason?: string
	reasonText?: string
}

interface FreezeAccountParams extends AccountFreezeDetails {
	accessToken: string
	account: Account
}

export interface AccountStatusChange extends AccountFreezeDetails {
	status: string
}

export async function freezeAccount({accessToken, account, reason, subReason, reasonText}: FreezeAccountParams) {
	const data = {
		type: "accountFreeze",
		attributes: {
			reason,
			subReason,
			reasonText,
		},
	}

	return await createResource<Account>(`accounts/${account.id}/freeze`, accessToken, data)
}

export async function unfreezeAccount(accessToken: string, account: Account) {
	const data = {
		type: "accountUnfreeze",
	}

	return await createResource<Account>(`accounts/${account.id}/unfreeze`, accessToken, data)
}

export async function getTotalDepositsChart(accessToken: string, since?: string, until?: string) {
	//Removing next line till the issue with accounts/org/total-deposit-chart will be solved
	return new Promise(() => {
		return {
			error: {
				errors: [
					{
						title: "Not Found",
						status: "404",
						detail: "",
					},
				],
			},
		}
	}) as Promise<Result<OkDocument<TotalDepositChartData>, ErrorDocument>>

	return await getResource<TotalDepositChartData>("accounts/org/total-deposit-chart", accessToken, {
		filter: {
			since,
			until,
		},
	})
}

export async function updateDepositAccount(
	accessToken: string,
	id: string,
	{depositProduct, name}: {depositProduct?: string; name?: string}
) {
	const result = await updateResource<Account>(`accounts/${id}/`, accessToken, {
		type: "depositAccount",
		attributes: Object.assign(
			{},
			depositProduct ? {depositProduct: depositProduct} : undefined,
			name ? {name: name} : undefined
		),
	})
	return result.map((v) => v.data)
}

export async function updateWalletAccount(
	accessToken: string,
	id: string,
	{walletTerms, name}: {walletTerms?: string; name?: string}
) {
	const result = await updateResource<Account>(`accounts/${id}/`, accessToken, {
		type: "walletAccount",
		attributes: Object.assign(
			{},
			walletTerms ? {walletTerms: walletTerms} : undefined,
			name ? {name: name} : undefined
		),
	})
	return result.map((v) => v.data)
}

export async function updateAccountTags(
	accessToken: string,
	accountId: string,
	accountType: "depositAccount" | "walletAccount",
	tags: {[key: string]: string | null}
) {
	const result = await updateResource<Account>(`accounts/${accountId}/`, accessToken, {
		type: accountType,
		attributes: {
			tags,
		},
	})
	return result.map((v) => v.data)
}

export async function addSecondaryAccountNumber(accessToken: string, id: string, bankRountingId: string) {
	const data = {
		relationships: {
			bankRouting: {
				data: {
					id: bankRountingId,
				},
			},
		},
	}

	return await createResource<Account>(`accounts/${id}/secondary-routing-number`, accessToken, data)
}

export async function createBatchAccount(accessToken: string, orgId: string, name: string, depositProduct: string) {
	const data = {
		type: "batchAccount",
		attributes: {
			depositProduct: depositProduct,
			name: name,
		},
		relationships: {
			org: {
				data: {
					id: orgId,
					type: "org",
				},
			},
		},
	}

	return await createResource<Account>("accounts", accessToken, data)
}

export async function getAccountLimits(accessToken: string, id: string) {
	const result = await getResource<Limits>(`accounts/${id}/limits`, accessToken)
	return result.map((v) => v.data)
}

export enum LimitOverrideType {
	CardDailyPurchase = "card-daily-purchase",
	CardDailyTransaction = "card-daily-transaction",
	CardDailyDeposit = "card-daily-deposit",
	CardDailyWithdrawal = "card-daily-withdrawal",
	AchDailyCredit = "ach-daily-credit",
	AchDailyDebit = "ach-daily-debit",
	AchMonthlyCredit = "ach-monthly-credit",
	AchMonthlyDebit = "ach-monthly-debit",
	WireDailyTransfer = "wire-daily-transfer",
	WireMonthlyTransfer = "wire-monthly-transfer",
	CheckDepositDaily = "check-deposit-daily",
	CheckDepositMonthly = "check-deposit-monthly",
	CheckPaymentDaily = "check-payment-daily",
	CheckPaymentMonthly = "check-payment-monthly",
}

export interface AchLimitOverrideAttributes {
	dailyDebitLimit?: number
	dailyCreditLimit?: number
	monthlyDebitLimit?: number
	monthlyCreditLimit?: number
	expiredAt?: string
	idempotencyKey: string
}

export type AchLimitOverrideUpdateAttributes = {
	dailyDebitLimit?: number | null
	dailyCreditLimit?: number | null
	monthlyDebitLimit?: number | null
	monthlyCreditLimit?: number | null
	expiredAt?: string
}

export interface AchLimitOverrideRequest {
	data: {
		type: "achLimitOverride"
		attributes: AchLimitOverrideAttributes
		relationships: {
			account: {
				data: {
					type: "account"
					id: string
				}
			}
		}
	}
}

export interface AchLimitOverrideResponse {
	id: string
	type: "achLimitOverride"
	attributes: AchLimitOverrideAttributes & {
		createdAt: string
	}
}

export interface CardLimitOverrideAttributes {
	dailyPurchaseLimit?: number
	dailyCardTransactionLimit?: number
	dailyDepositLimit?: number
	dailyWithdrawalLimit?: number
	idempotencyKey: string
}

export interface CardLimitOverrideRequest {
	data: {
		type: "cardLimitOverride"
		attributes: CardLimitOverrideAttributes
		relationships: {
			account: {
				data: {
					type: "account"
					id: string
				}
			}
		}
	}
}

export interface WireLimitOverrideAttributes {
	dailyLimit?: number
	monthlyLimit?: number
	expiredAt?: string
	idempotencyKey: string
}

export type WireLimitOverrideUpdateAttributes = {
	dailyLimit?: number | null
	monthlyLimit?: number | null
	expiredAt?: string
}

export interface WireLimitOverrideResponse {
	id: string
	type: "wireLimitOverride"
	attributes: WireLimitOverrideAttributes & {
		createdAt: string
	}
}

export interface CheckDepositLimitOverrideAttributes {
	dailyLimit?: number
	monthlyLimit?: number
	expiredAt?: string
	idempotencyKey: string
}

export type CheckDepositLimitOverrideUpdateAttributes = {
	dailyLimit?: number | null
	monthlyLimit?: number | null
	expiredAt?: string
}

export interface CheckDepositLimitOverrideResponse {
	id: string
	type: "checkDepositLimitOverride"
	attributes: CheckDepositLimitOverrideAttributes & {
		createdAt: string
	}
}

export interface CheckPaymentLimitOverrideAttributes {
	dailyLimit?: number
	monthlyLimit?: number
	expiredAt?: string
	idempotencyKey: string
}

export type CheckPaymentLimitOverrideUpdateAttributes = {
	dailyLimit?: number | null
	monthlyLimit?: number | null
	expiredAt?: string
}

export interface CheckPaymentLimitOverrideResponse {
	id: string
	type: "checkPaymentLimitOverride"
	attributes: CheckPaymentLimitOverrideAttributes & {
		createdAt: string
	}
}

export interface LimitOverrideData {
	limitType: LimitOverrideType
	amount: number
	endDate?: string
}

function generateIdempotencyKey(prefix: string): string {
	return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

export async function createAchLimitOverride(
	accessToken: string,
	accountId: string,
	attributes: Omit<AchLimitOverrideAttributes, "idempotencyKey">
): Promise<Result<AchLimitOverrideResponse, ErrorDocument>> {
	const requestBody = {
		type: "achLimitOverride",
		attributes: {
			...attributes,
			idempotencyKey: generateIdempotencyKey("ach-override"),
		},
		relationships: {
			account: {
				data: {
					type: "account",
					id: accountId,
				},
			},
		},
	}

	const result = await createResource<AchLimitOverrideResponse>("account-config-overrides", accessToken, requestBody)
	return result.map((v) => v.data)
}

export async function updateAchLimitOverride(
	accessToken: string,
	overrideId: string,
	attributes: AchLimitOverrideUpdateAttributes
): Promise<Result<AchLimitOverrideResponse, ErrorDocument>> {
	const requestBody = {
		type: "achLimitOverride",
		attributes,
	}

	const result = await updateResource<AchLimitOverrideResponse>(
		`account-config-overrides/${overrideId}`,
		accessToken,
		requestBody
	)
	return result.map((v) => v.data)
}

export async function createWireLimitOverride(
	accessToken: string,
	accountId: string,
	attributes: Omit<WireLimitOverrideAttributes, "idempotencyKey">
): Promise<Result<WireLimitOverrideResponse, ErrorDocument>> {
	const requestBody = {
		type: "wireLimitOverride",
		attributes: {
			...attributes,
			idempotencyKey: generateIdempotencyKey("wire-override"),
		},
		relationships: {
			account: {
				data: {
					type: "account",
					id: accountId,
				},
			},
		},
	}

	const result = await createResource<WireLimitOverrideResponse>("account-config-overrides", accessToken, requestBody)
	return result.map((v) => v.data)
}

export async function updateWireLimitOverride(
	accessToken: string,
	overrideId: string,
	attributes: WireLimitOverrideUpdateAttributes
): Promise<Result<WireLimitOverrideResponse, ErrorDocument>> {
	const requestBody = {
		type: "wireLimitOverride",
		attributes,
	}

	const result = await updateResource<WireLimitOverrideResponse>(
		`account-config-overrides/${overrideId}`,
		accessToken,
		requestBody
	)
	return result.map((v) => v.data)
}

export async function createCheckDepositLimitOverride(
	accessToken: string,
	accountId: string,
	attributes: Omit<CheckDepositLimitOverrideAttributes, "idempotencyKey">
): Promise<Result<CheckDepositLimitOverrideResponse, ErrorDocument>> {
	const requestBody = {
		type: "checkDepositLimitOverride",
		attributes: {
			...attributes,
			idempotencyKey: generateIdempotencyKey("check-deposit-override"),
		},
		relationships: {
			account: {
				data: {
					type: "account",
					id: accountId,
				},
			},
		},
	}

	const result = await createResource<CheckDepositLimitOverrideResponse>(
		"account-config-overrides",
		accessToken,
		requestBody
	)
	return result.map((v) => v.data)
}

export async function updateCheckDepositLimitOverride(
	accessToken: string,
	overrideId: string,
	attributes: CheckDepositLimitOverrideUpdateAttributes
): Promise<Result<CheckDepositLimitOverrideResponse, ErrorDocument>> {
	const requestBody = {
		type: "checkDepositLimitOverride",
		attributes,
	}

	const result = await updateResource<CheckDepositLimitOverrideResponse>(
		`account-config-overrides/${overrideId}`,
		accessToken,
		requestBody
	)
	return result.map((v) => v.data)
}

export async function createCheckPaymentLimitOverride(
	accessToken: string,
	accountId: string,
	attributes: Omit<CheckPaymentLimitOverrideAttributes, "idempotencyKey">
): Promise<Result<CheckPaymentLimitOverrideResponse, ErrorDocument>> {
	const requestBody = {
		type: "checkPaymentLimitOverride",
		attributes: {
			...attributes,
			idempotencyKey: generateIdempotencyKey("check-payment-override"),
		},
		relationships: {
			account: {
				data: {
					type: "account",
					id: accountId,
				},
			},
		},
	}

	const result = await createResource<CheckPaymentLimitOverrideResponse>(
		"account-config-overrides",
		accessToken,
		requestBody
	)
	return result.map((v) => v.data)
}

export async function updateCheckPaymentLimitOverride(
	accessToken: string,
	overrideId: string,
	attributes: CheckPaymentLimitOverrideUpdateAttributes
): Promise<Result<CheckPaymentLimitOverrideResponse, ErrorDocument>> {
	const requestBody = {
		type: "checkPaymentLimitOverride",
		attributes,
	}

	const result = await updateResource<CheckPaymentLimitOverrideResponse>(
		`account-config-overrides/${overrideId}`,
		accessToken,
		requestBody
	)
	return result.map((v) => v.data)
}

export async function cancelLimitOverride(
	accessToken: string,
	overrideId: string
): Promise<Result<void, ErrorDocument>> {
	const result = await createResource<void>(`account-config-overrides/${overrideId}/cancel`, accessToken, {})
	return result.map(() => undefined)
}

function getLimitAttributeName(limitType: LimitOverrideType): keyof AchLimitOverrideUpdateAttributes | null {
	switch (limitType) {
		case "ach-daily-credit":
			return "dailyCreditLimit"
		case "ach-daily-debit":
			return "dailyDebitLimit"
		case "ach-monthly-credit":
			return "monthlyCreditLimit"
		case "ach-monthly-debit":
			return "monthlyDebitLimit"
		case "card-daily-purchase":
		case "card-daily-transaction":
		case "card-daily-deposit":
		case "card-daily-withdrawal":
			return null
		default:
			return null
	}
}

export async function saveAccountLimitOverride(
	accessToken: string,
	accountId: string,
	data: LimitOverrideData,
	existingOverrideId?: string
): Promise<Result<AchLimitOverrideResponse, ErrorDocument>> {
	const {limitType, amount, endDate} = data

	const attributeName = getLimitAttributeName(limitType)
	if (!attributeName) {
		// Card limits not yet implemented
		return Promise.reject(new Error("Card limit override not yet implemented"))
	}

	const attributes: AchLimitOverrideUpdateAttributes = {
		[attributeName]: amount,
	}
	if (endDate) {
		attributes.expiredAt = endDate
	}

	if (existingOverrideId) {
		return updateAchLimitOverride(accessToken, existingOverrideId, attributes)
	} else {
		return createAchLimitOverride(
			accessToken,
			accountId,
			attributes as Omit<AchLimitOverrideAttributes, "idempotencyKey">
		)
	}
}

export async function revertSingleLimitOverride(
	accessToken: string,
	overrideId: string,
	limitType: LimitOverrideType,
	accountId: string,
	currentOverride: AccountConfigOverride
): Promise<Result<unknown, ErrorDocument>> {
	const cancelResult = await cancelLimitOverride(accessToken, overrideId)
	if (!cancelResult.isOk()) return cancelResult

	const attrs = currentOverride.attributes

	if (currentOverride.type === "achLimitOverride") {
		const attributeName = getLimitAttributeName(limitType)
		if (!attributeName) return cancelResult

		const remaining: Omit<AchLimitOverrideAttributes, "idempotencyKey"> = {}
		if (attributeName !== "dailyCreditLimit" && attrs.dailyCreditLimit != null)
			remaining.dailyCreditLimit = attrs.dailyCreditLimit
		if (attributeName !== "dailyDebitLimit" && attrs.dailyDebitLimit != null)
			remaining.dailyDebitLimit = attrs.dailyDebitLimit
		if (attributeName !== "monthlyCreditLimit" && attrs.monthlyCreditLimit != null)
			remaining.monthlyCreditLimit = attrs.monthlyCreditLimit
		if (attributeName !== "monthlyDebitLimit" && attrs.monthlyDebitLimit != null)
			remaining.monthlyDebitLimit = attrs.monthlyDebitLimit
		if (attrs.expiredAt) remaining.expiredAt = attrs.expiredAt

		const hasLimitAttrs =
			remaining.dailyCreditLimit != null ||
			remaining.dailyDebitLimit != null ||
			remaining.monthlyCreditLimit != null ||
			remaining.monthlyDebitLimit != null
		if (!hasLimitAttrs) return cancelResult
		return createAchLimitOverride(accessToken, accountId, remaining)
	} else if (currentOverride.type === "wireLimitOverride") {
		const attrName = getWireLimitAttributeName(limitType as WireLimitOverrideData["limitType"])

		const remaining: Omit<WireLimitOverrideAttributes, "idempotencyKey"> = {}
		if (attrName !== "dailyLimit" && attrs.dailyLimit != null) remaining.dailyLimit = attrs.dailyLimit
		if (attrName !== "monthlyLimit" && attrs.monthlyLimit != null) remaining.monthlyLimit = attrs.monthlyLimit
		if (attrs.expiredAt) remaining.expiredAt = attrs.expiredAt

		const hasLimitAttrs = remaining.dailyLimit != null || remaining.monthlyLimit != null
		if (!hasLimitAttrs) return cancelResult
		return createWireLimitOverride(accessToken, accountId, remaining)
	} else if (currentOverride.type === "checkDepositLimitOverride") {
		const attrName = getCheckDepositLimitAttributeName(limitType as CheckDepositLimitOverrideData["limitType"])

		const remaining: Omit<CheckDepositLimitOverrideAttributes, "idempotencyKey"> = {}
		if (attrName !== "dailyLimit" && attrs.dailyLimit != null) remaining.dailyLimit = attrs.dailyLimit
		if (attrName !== "monthlyLimit" && attrs.monthlyLimit != null) remaining.monthlyLimit = attrs.monthlyLimit
		if (attrs.expiredAt) remaining.expiredAt = attrs.expiredAt

		const hasLimitAttrs = remaining.dailyLimit != null || remaining.monthlyLimit != null
		if (!hasLimitAttrs) return cancelResult
		return createCheckDepositLimitOverride(accessToken, accountId, remaining)
	} else if (currentOverride.type === "checkPaymentLimitOverride") {
		const attrName = getCheckPaymentLimitAttributeName(limitType as CheckPaymentLimitOverrideData["limitType"])

		const remaining: Omit<CheckPaymentLimitOverrideAttributes, "idempotencyKey"> = {}
		if (attrName !== "dailyLimit" && attrs.dailyLimit != null) remaining.dailyLimit = attrs.dailyLimit
		if (attrName !== "monthlyLimit" && attrs.monthlyLimit != null) remaining.monthlyLimit = attrs.monthlyLimit
		if (attrs.expiredAt) remaining.expiredAt = attrs.expiredAt

		const hasLimitAttrs = remaining.dailyLimit != null || remaining.monthlyLimit != null
		if (!hasLimitAttrs) return cancelResult
		return createCheckPaymentLimitOverride(accessToken, accountId, remaining)
	}
	return cancelResult
}

export interface WireLimitOverrideData {
	limitType: LimitOverrideType.WireDailyTransfer | LimitOverrideType.WireMonthlyTransfer
	amount: number
	endDate?: string
}

function getWireLimitAttributeName(
	limitType: WireLimitOverrideData["limitType"]
): keyof WireLimitOverrideUpdateAttributes {
	switch (limitType) {
		case LimitOverrideType.WireDailyTransfer:
			return "dailyLimit"
		case LimitOverrideType.WireMonthlyTransfer:
			return "monthlyLimit"
	}
}

export async function saveWireLimitOverride(
	accessToken: string,
	accountId: string,
	data: WireLimitOverrideData,
	existingOverrideId?: string
): Promise<Result<WireLimitOverrideResponse, ErrorDocument>> {
	const {limitType, amount, endDate} = data

	const attributeName = getWireLimitAttributeName(limitType)

	const attributes: WireLimitOverrideUpdateAttributes = {
		[attributeName]: amount,
	}
	if (endDate) {
		attributes.expiredAt = endDate
	}

	if (existingOverrideId) {
		return updateWireLimitOverride(accessToken, existingOverrideId, attributes)
	} else {
		return createWireLimitOverride(
			accessToken,
			accountId,
			attributes as Omit<WireLimitOverrideAttributes, "idempotencyKey">
		)
	}
}

export interface CheckDepositLimitOverrideData {
	limitType: LimitOverrideType.CheckDepositDaily | LimitOverrideType.CheckDepositMonthly
	amount: number
	endDate?: string
}

function getCheckDepositLimitAttributeName(
	limitType: CheckDepositLimitOverrideData["limitType"]
): keyof CheckDepositLimitOverrideUpdateAttributes {
	switch (limitType) {
		case LimitOverrideType.CheckDepositDaily:
			return "dailyLimit"
		case LimitOverrideType.CheckDepositMonthly:
			return "monthlyLimit"
	}
}

export async function saveCheckDepositLimitOverride(
	accessToken: string,
	accountId: string,
	data: CheckDepositLimitOverrideData,
	existingOverrideId?: string
): Promise<Result<CheckDepositLimitOverrideResponse, ErrorDocument>> {
	const {limitType, amount, endDate} = data

	const attributeName = getCheckDepositLimitAttributeName(limitType)

	const attributes: CheckDepositLimitOverrideUpdateAttributes = {
		[attributeName]: amount,
	}
	if (endDate) {
		attributes.expiredAt = endDate
	}

	if (existingOverrideId) {
		return updateCheckDepositLimitOverride(accessToken, existingOverrideId, attributes)
	} else {
		return createCheckDepositLimitOverride(
			accessToken,
			accountId,
			attributes as Omit<CheckDepositLimitOverrideAttributes, "idempotencyKey">
		)
	}
}

export interface CheckPaymentLimitOverrideData {
	limitType: LimitOverrideType.CheckPaymentDaily | LimitOverrideType.CheckPaymentMonthly
	amount: number
	endDate?: string
}

function getCheckPaymentLimitAttributeName(
	limitType: CheckPaymentLimitOverrideData["limitType"]
): keyof CheckPaymentLimitOverrideUpdateAttributes {
	switch (limitType) {
		case LimitOverrideType.CheckPaymentDaily:
			return "dailyLimit"
		case LimitOverrideType.CheckPaymentMonthly:
			return "monthlyLimit"
	}
}

export async function saveCheckPaymentLimitOverride(
	accessToken: string,
	accountId: string,
	data: CheckPaymentLimitOverrideData,
	existingOverrideId?: string
): Promise<Result<CheckPaymentLimitOverrideResponse, ErrorDocument>> {
	const {limitType, amount, endDate} = data

	const attributeName = getCheckPaymentLimitAttributeName(limitType)

	const attributes: CheckPaymentLimitOverrideUpdateAttributes = {
		[attributeName]: amount,
	}
	if (endDate) {
		attributes.expiredAt = endDate
	}

	if (existingOverrideId) {
		return updateCheckPaymentLimitOverride(accessToken, existingOverrideId, attributes)
	} else {
		return createCheckPaymentLimitOverride(
			accessToken,
			accountId,
			attributes as Omit<CheckPaymentLimitOverrideAttributes, "idempotencyKey">
		)
	}
}

export type OverrideStatus = "active" | "canceled" | "expired"

export interface AccountConfigOverride {
	id: string
	type:
		| "achLimitOverride"
		| "cardLimitOverride"
		| "wireLimitOverride"
		| "checkDepositLimitOverride"
		| "checkPaymentLimitOverride"
	attributes: {
		// ACH attributes
		dailyDebitLimit?: number
		dailyCreditLimit?: number
		monthlyDebitLimit?: number
		monthlyCreditLimit?: number
		// Card attributes
		dailyPurchaseLimit?: number
		dailyCardTransactionLimit?: number
		dailyDepositLimit?: number
		dailyWithdrawalLimit?: number
		// Wire, Check Deposit, Check Payment attributes
		dailyLimit?: number
		monthlyLimit?: number
		expiredAt?: string
		status?: OverrideStatus
		createdAt: string
	}
	relationships: {
		account: {
			data: {
				type: "account"
				id: string
			}
		}
	}
}

export function isOverrideActive(override: AccountConfigOverride): boolean {
	const status = override.attributes.status
	if (!status) return true
	return status.toLowerCase() === "active"
}

export async function getAccountConfigOverrides(
	accessToken: string,
	accountId: string
): Promise<Result<AccountConfigOverride[], ErrorDocument>> {
	const query = {
		filter: {
			accountId,
		},
	}

	const result = await getResource<AccountConfigOverride[]>("account-config-overrides", accessToken, query)
	return result.map((v) => v.data)
}

export function getAchOverrideId(overrides: AccountConfigOverride[]): string | undefined {
	const achOverride = overrides.find((o) => o.type === "achLimitOverride" && isOverrideActive(o))
	return achOverride?.id
}

export function getWireOverrideId(overrides: AccountConfigOverride[]): string | undefined {
	const wireOverride = overrides.find((o) => o.type === "wireLimitOverride" && isOverrideActive(o))
	return wireOverride?.id
}

export function getCheckDepositOverrideId(overrides: AccountConfigOverride[]): string | undefined {
	const checkDepositOverride = overrides.find((o) => o.type === "checkDepositLimitOverride" && isOverrideActive(o))
	return checkDepositOverride?.id
}

export function getCheckPaymentOverrideId(overrides: AccountConfigOverride[]): string | undefined {
	const checkPaymentOverride = overrides.find((o) => o.type === "checkPaymentLimitOverride" && isOverrideActive(o))
	return checkPaymentOverride?.id
}

export async function getCreditAccounts(accessToken: string, orgId: string) {
	const query = {
		page: {
			limit: 1000,
			offset: 0,
		},
		filter: {
			orgId: orgId,
			type: AccountType.credit,
		},
	}

	const result = await getResource<Array<Account>>("accounts", accessToken, query)
	return result.map((v) => v.data.filter(isCreditAccount))
}

export async function getOrgAccountsTotalCreditLimit(accessToken: string, bankId: string) {
	const query = {bankId}
	const result = await getResource<AccountsTotalCreditLimit>("accounts/org/total-credit-limit", accessToken, query)
	return result.map((v) => v.data)
}
